import axios, { AxiosRequestConfig } from 'axios';
import { DataResponse, PagedResponse } from '../types/api';

const REVIEWS_URL = '/v2/admin/bd-fraud/reviews';
const ENFORCEMENT_URL = '/v2/admin/bd-fraud/enforcement-mode';
const SCANS_URL = '/v2/admin/bd-fraud/scans';
const UNREVIEWED_COUNT_URL = '/v2/admin/bd-fraud/unreviewed-count';
const ALLOWLIST_URL = '/v2/admin/bd-fraud/allowlist';

export type BdReviewTier = 'CONFIRMED' | 'MEDIUM' | 'LOW';

export type BdReviewOutcome =
  | 'ALLOWED'
  | 'OBSERVED'
  | 'HELD_FOR_REVIEW'
  | 'BLOCKED'
  | 'RETROACTIVE_SCAN';

export type BdReviewResolution = 'CONFIRMED_FRAUD' | 'LEGITIMATE';

export type BdFraudEnforcementMode = 'OFF' | 'OBSERVE' | 'ENFORCE';

export type BdSignalType =
  | 'EMAIL'
  | 'PHONE'
  | 'NAME'
  | 'ADDRESS'
  | 'IP'
  | 'DEVICE_TOKEN'
  | 'USER_AGENT';

export interface BdMatchedSignal {
  signalType: BdSignalType;
  signalValue: string;
  weight: number;
}

export interface BdUserSummary {
  id: number;
  fullName: string | null;
  email: string | null;
  signupDate: number | null;
  latestNotes: string[];
  blocked: boolean;
}

export interface BdReviewLog {
  id: number;
  bookingId: number | null;
  payorUserId: number;
  payeeUserId: number;
  score: number;
  tier: BdReviewTier;
  matchedSignals: BdMatchedSignal[];
  outcome: BdReviewOutcome;
  reviewed: boolean;
  reviewedBy: number | null;
  reviewedAt: string | null;
  reviewResolution: BdReviewResolution | null;
  reviewNotes: string | null;
  scanHash: string | null;
  created: string;
  payorSummary: BdUserSummary | null;
  payeeSummary: BdUserSummary | null;
  payorCompanyName: string | null;
}

export interface BdReviewFilter {
  tier?: BdReviewTier;
  outcome?: BdReviewOutcome;
  reviewed?: boolean;
  payorUserId?: number;
  payeeUserId?: number;
  bookingId?: number;
  createdStart?: string;
  createdEnd?: string;
  limit?: number;
  offset?: number;
}

export interface BdScanResult {
  bookingsScanned: number;
  rowsRecorded: number;
  tierBreakdown: Record<string, number>;
}

export interface BdAllowlistEntry {
  id: number;
  payorUserId: number;
  payeeUserId: number;
  createdBy: number;
  notes: string | null;
  created: string;
}

class BdFraudService {
  static list(
    params: BdReviewFilter,
    config?: AxiosRequestConfig
  ): Promise<PagedResponse<BdReviewLog>> {
    return axios.get(REVIEWS_URL, { ...config, params });
  }

  static get(id: number, config?: AxiosRequestConfig): Promise<DataResponse<BdReviewLog>> {
    return axios.get(`${REVIEWS_URL}/${id}`, config);
  }

  static resolve(
    id: number,
    resolution: BdReviewResolution,
    notes?: string,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<BdReviewLog>> {
    return axios.patch(`${REVIEWS_URL}/${id}/resolve`, { resolution, notes }, config);
  }

  static unreviewedCount(config?: AxiosRequestConfig): Promise<DataResponse<number>> {
    return axios.get(UNREVIEWED_COUNT_URL, config);
  }

  static getEnforcementMode(
    config?: AxiosRequestConfig
  ): Promise<DataResponse<{ mode: BdFraudEnforcementMode }>> {
    return axios.get(ENFORCEMENT_URL, config);
  }

  static setEnforcementMode(
    mode: BdFraudEnforcementMode,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<{ mode: BdFraudEnforcementMode }>> {
    return axios.put(ENFORCEMENT_URL, { mode }, config);
  }

  static runScan(
    startDate: string,
    endDate: string,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<BdScanResult>> {
    return axios.post(SCANS_URL, { startDate, endDate }, config);
  }

  static listAllowlist(
    limit = 100,
    offset = 0,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<BdAllowlistEntry[]>> {
    return axios.get(ALLOWLIST_URL, { ...config, params: { limit, offset } });
  }

  static addAllowlist(
    payorUserId: number,
    payeeUserId: number,
    notes: string | undefined,
    config?: AxiosRequestConfig
  ): Promise<DataResponse<BdAllowlistEntry>> {
    return axios.post(ALLOWLIST_URL, { payorUserId, payeeUserId, notes }, config);
  }

  static removeAllowlist(id: number, config?: AxiosRequestConfig): Promise<DataResponse<boolean>> {
    return axios.delete(`${ALLOWLIST_URL}/${id}`, config);
  }
}

export { BdFraudService };
